import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { graphql, withApollo } from 'react-apollo';
import { compose, mapProps } from 'recompose';
import PropTypes from 'prop-types';

import InputField from 'components/common/InputField/InputField';
import { createEmailValidationFn } from 'utils/validations/validations';
import { NO_CACHE } from 'graphql/fetchPolicies';
import { getObjValue } from 'utils/functions';
import {
  updateTextFieldValue,
  userStartTyping as userStartTypingMut,
  userStopTyping as userStopTypingMut,
  getLatestAssessmentEventById,
  ddeActionsTriggered
} from 'graphql/operations';
import { EMAIL_ADDRESS } from 'components/threepp/assessment/Answers/Text/InputMask';
import { useDebouncedStartTyping, useDebouncedStopTyping } from 'components/threepp/assessment/Answers/helper';
import { DisplayControlType } from 'utils/constants';
import TextArea from 'components/common/TextArea/TextArea';
import { decodeHtml, encodeHtml } from 'utils/htmlEncoderDecoder';
import SaveAnswerHOC from '../SaveAnswerHOC';
import EventHandlerHOC from '../FieldUpdatesHOC';

import './TextAnswer.scss';

const getValidationFunction = mask => {
  switch (mask) {
    case EMAIL_ADDRESS:
      return createEmailValidationFn(null, true);
    default:
      return () => {
        return null;
      };
  }
};

export function TextAnswer(props) {
  const { save, disabled, question, latestEvent, assessmentId, userStartTyping, userStopTyping } = props;
  const { t } = useTranslation();
  const { defaultText, textValue, mask, maxCharactersAllowed, calculated, displayControlType } = question;
  const [error, setError] = useState(null);

  const action = getObjValue(latestEvent, 'action');
  const isDisabled = disabled || action === 'START_TYPING';

  const userStartTypingCallback = useDebouncedStartTyping(userStartTyping, assessmentId, question.id);
  const userStopTypingCallback = useDebouncedStopTyping(userStopTyping, assessmentId, question.id);

  const [inputValue, setInputValue] = useState('');
  const [isUpdated, setIsUpdated] = useState(false);

  const handleValidationErrorCallback = useCallback(
    e => {
      const normalized = e.value && e.value.trim() === '' ? null : e.value;
      save(normalized);
    },
    [save]
  );

  const minRows = mask ? 1 : 4;
  const maxRows = mask ? 1 : 16;
  const inputValidation = getValidationFunction(mask);

  const handleChangeCallback = useCallback(
    input => {
      setInputValue(input);
      setIsUpdated(true);
      userStartTypingCallback();
      userStopTypingCallback();
      let normalized = null;
      if (displayControlType !== DisplayControlType.TEXTAREA) {
        const encodedInput = encodeHtml(input);
        normalized = encodedInput.trim() === '' ? null : encodedInput;
      } else {
        normalized = input.trim() === '' ? null : input;
      }
      save(normalized);
    },
    [displayControlType, save, userStartTypingCallback, userStopTypingCallback]
  );

  useEffect(() => {
    let errorObj = null;
    if (inputValidation && textValue) {
      errorObj = textValue ? inputValidation(textValue.toString()) : null;
    }
    setError(errorObj ? t(errorObj.key, { ...errorObj.args }) : null);
  }, [t, inputValidation, textValue]);

  return (
    <>
      {displayControlType === DisplayControlType.TEXTAREA ? (
        <TextArea
          t={t}
          editorHeight={200}
          value={isUpdated && !latestEvent ? inputValue : textValue}
          placeholder={defaultText}
          disabled={calculated || isDisabled}
          onChange={handleChangeCallback}
          onValidationError={handleValidationErrorCallback}
          maxCharactersAllowed={maxCharactersAllowed}
        />
      ) : (
        <InputField
          t={t}
          name="answer"
          classes={`text-field-answer ${minRows > 1 ? 'multiline' : ''}`}
          fullWidth
          type="text"
          value={isUpdated && !latestEvent ? inputValue : decodeHtml(textValue)}
          placeholder={defaultText}
          onFocus={e => {
            e.target.placeholder = '';
          }}
          onBlur={e => {
            e.target.placeholder = defaultText || '';
          }}
          minRows={minRows}
          maxRows={maxRows}
          multiline={minRows > 1}
          disabled={calculated || isDisabled}
          inputValidation={inputValidation}
          onChange={handleChangeCallback}
          maxCharactersAllowed={maxCharactersAllowed}
          onValidationError={handleValidationErrorCallback}
          error={error}
        />
      )}
    </>
  );
}

TextAnswer.defaultProps = {
  latestEvent: { action: '' }
};

TextAnswer.propTypes = {
  // TODO: Fix this...
  /* eslint-disable react/forbid-prop-types */
  question: PropTypes.object.isRequired,
  save: PropTypes.any.isRequired,
  disabled: PropTypes.bool.isRequired,
  userStartTyping: PropTypes.func.isRequired,
  userStopTyping: PropTypes.func.isRequired,
  latestEvent: PropTypes.object
  /* eslint-enable react/forbid-prop-types */
};

export const TextAnswerHOC = compose(
  withApollo,
  graphql(updateTextFieldValue, { name: 'fieldMutation' }),
  graphql(userStartTypingMut, { name: 'userStartTyping' }),
  graphql(userStopTypingMut, { name: 'userStopTyping' }),
  graphql(ddeActionsTriggered, { name: 'ddeActionsTriggered' }),
  graphql(getLatestAssessmentEventById, {
    options: props => ({
      variables: {
        fieldId: props.question.id
      },
      fetchPolicy: NO_CACHE
    }),
    name: 'getLatestAssessmentEventById'
  }),
  SaveAnswerHOC,
  EventHandlerHOC,
  mapProps(props => ({
    question: props.question,
    disabled: props.disabled,
    assessmentId: props.assessmentId,
    invokeActionsForFields: props.invokeActionsForFields,
    save: props.save,
    userStartTyping: props.userStartTyping,
    userStopTyping: props.userStopTyping,
    latestEvent: props.getLatestAssessmentEventById.getLatestAssessmentEventById
  }))
);

export default TextAnswerHOC(TextAnswer);
